import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Award, ArrowRight } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { delay, duration: 1, ease: [0.16, 1, 0.3, 1] },
});

export default function Footer() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Inquiry received", {
        description: "Thank you — our team will personally reply within one business day.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Unable to send inquiry", {
        description: "Please call us directly at 613-716-1119.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <footer
      id="contact"
      data-testid="contact-footer"
      className="relative border-t border-gold/15 bg-navy-950"
    >
      <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-10 md:py-36">
        <div className="grid gap-20 lg:grid-cols-2 lg:gap-28">
          <div>
            <motion.p {...fadeUp(0)} className="text-[0.6rem] font-semibold uppercase tracking-[0.45em] text-gold md:text-xs">
              Private Consultations
            </motion.p>
            <motion.h2
              {...fadeUp(0.1)}
              className="mt-5 font-serif text-2xl font-semibold leading-tight text-white sm:text-3xl lg:text-4xl"
              data-testid="contact-heading"
            >
              Begin Your
              <br />
              Transformation.
            </motion.h2>
            <motion.p {...fadeUp(0.18)} className="mt-7 max-w-md text-sm font-light leading-relaxed text-slate-400">
              Every Aspen project begins with a private conversation. Share your
              vision — we will respond with honesty, precision and a plan.
            </motion.p>

            <motion.div {...fadeUp(0.26)} className="mt-12 space-y-5">
              <a
                href={CONTACT.phoneHref}
                data-testid="footer-phone-link"
                className="group flex items-center gap-4 text-sm font-light tracking-wide text-slate-300 transition-colors duration-500 hover:text-gold"
              >
                <span className="flex h-10 w-10 items-center justify-center border border-white/10 transition-colors duration-500 group-hover:border-gold/50">
                  <Phone className="h-4 w-4 text-gold" strokeWidth={1.25} />
                </span>
                {CONTACT.phone}
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="footer-email-link"
                className="group flex items-center gap-4 text-sm font-light tracking-wide text-slate-300 transition-colors duration-500 hover:text-gold"
              >
                <span className="flex h-10 w-10 items-center justify-center border border-white/10 transition-colors duration-500 group-hover:border-gold/50">
                  <Mail className="h-4 w-4 text-gold" strokeWidth={1.25} />
                </span>
                {CONTACT.email}
              </a>
              <div
                data-testid="footer-address"
                className="flex items-center gap-4 text-sm font-light tracking-wide text-slate-300"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/10">
                  <MapPin className="h-4 w-4 text-gold" strokeWidth={1.25} />
                </span>
                {CONTACT.address}
              </div>
            </motion.div>

            <motion.div
              {...fadeUp(0.34)}
              data-testid="trust-badge"
              className="mt-12 flex max-w-md items-center gap-5 border border-gold/25 bg-navy-900/60 p-6 backdrop-blur-xl"
            >
              <Award className="h-9 w-9 shrink-0 text-gold" strokeWidth={1} />
              <div>
                <p className="font-serif text-base font-semibold text-white">
                  15+ Years of Excellence
                </p>
                <p className="mt-1 text-[0.65rem] font-light uppercase tracking-[0.25em] text-slate-400">
                  Ottawa · Gatineau · Kingston · Cornwall
                </p>
              </div>
            </motion.div>
          </div>

          <motion.form
            {...fadeUp(0.15)}
            onSubmit={submit}
            data-testid="contact-form"
            className="flex flex-col justify-center"
          >
            <div className="grid gap-8 sm:grid-cols-2">
              <input
                required
                value={form.name}
                onChange={set("name")}
                placeholder="FULL NAME"
                aria-label="Full name"
                data-testid="contact-input-name"
                className="input-line"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="EMAIL ADDRESS"
                aria-label="Email address"
                data-testid="contact-input-email"
                className="input-line"
              />
            </div>
            <input
              value={form.phone}
              onChange={set("phone")}
              placeholder="PHONE (OPTIONAL)"
              aria-label="Phone number"
              data-testid="contact-input-phone"
              className="input-line mt-8"
            />
            <textarea
              required
              rows={3}
              value={form.message}
              onChange={set("message")}
              placeholder="TELL US ABOUT YOUR PROJECT"
              aria-label="Project details"
              data-testid="contact-input-message"
              className="input-line mt-8 resize-none"
            />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group relative mt-12 self-start overflow-hidden border border-gold/60 px-10 py-4 disabled:opacity-60"
            >
              <span className="relative z-10 flex items-center gap-3 text-[0.65rem] font-semibold uppercase tracking-[0.3em] text-white transition-colors duration-500 group-hover:text-navy-900">
                {sending ? "Sending…" : "Request a Consultation"}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
              </span>
              <span className="absolute inset-0 origin-left scale-x-0 bg-gold transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-x-100" />
            </button>
          </motion.form>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-6 px-6 py-8 md:flex-row md:items-center md:px-10">
          <Logo />
          <p className="text-[0.6rem] font-light uppercase tracking-[0.3em] text-slate-500" data-testid="footer-copyright">
            © 2026 Aspen Design — Crafted in Ottawa
          </p>
        </div>
      </div>
    </footer>
  );
}
